type AnalyticsData = Record<string, string | number | boolean>;

declare global {
  interface Window {
    umami?: {
      track(name: string, data?: AnalyticsData): void;
    };
    gtag?: {
      (command: "event", name: string, params?: AnalyticsData): void;
      (command: "set", params: AnalyticsData): void;
    };
  }
}

const INTERNAL_TRAFFIC_KEY = "oal_internal";
let lastPageLocation: string | null = null;

/**
 * Send a named event to the analytics tools loaded on the page.
 * Google Analytics (gtag, loaded in index.html) always receives it; Umami does too
 * when its script is present.
 */
export function trackEvent(name: string, data?: AnalyticsData): void {
  if (typeof window === "undefined") return;

  try {
    window.umami?.track(name, data);
  } catch {
    // Analytics must never break the app.
  }

  try {
    window.gtag?.("event", name, data);
  } catch {
    // Analytics must never break the app.
  }
}

/**
 * Send one GA4 page view for the current URL and title. The tag's automatic page
 * views are off (client/index.html) because the site rewrites its URL on every
 * filter and search change; App.tsx calls this once per page instead.
 */
export function trackPageView(): void {
  if (typeof window === "undefined") return;
  const pageLocation = window.location.href;

  try {
    window.gtag?.("event", "page_view", {
      page_location: pageLocation,
      page_title: document.title,
      ...(lastPageLocation ? { page_referrer: lastPageLocation } : {}),
    });
  } catch {
    // Analytics must never break the app.
  }

  lastPageLocation = pageLocation;
}

/**
 * Mark this browser as the site owner's. GA4 then tags its hits as internal
 * traffic: index.html reads the flag on every later page load, and GA4's
 * "Internal Traffic" data filter leaves those hits out of reports.
 */
export function markDeviceAsInternalTraffic(): void {
  try {
    localStorage.setItem(INTERNAL_TRAFFIC_KEY, "1");
  } catch {
    // Storage blocked: only this page load is marked (below).
  }

  try {
    window.gtag?.("set", { traffic_type: "internal" });
  } catch {
    // Analytics must never break the app.
  }
}

function destinationDomain(destination: string | null): string {
  if (!destination) return "none";
  try {
    return new URL(destination).hostname.replace(/^www\./, "");
  } catch {
    return "unknown";
  }
}

/**
 * Report a click-through to an event's own listing. Those links go through our
 * /api/outbound redirect, so Google Analytics can't tell they leave the site;
 * this sends the details carried in the redirect URL instead.
 */
export function trackOutboundClick(outboundHref: string): void {
  let params: URLSearchParams;
  try {
    params = new URL(outboundHref, window.location.origin).searchParams;
  } catch {
    return;
  }

  trackEvent("outbound_click", {
    surface: params.get("interactionSource") ?? "unknown",
    category: params.get("category") ?? "none",
    region: params.get("region") ?? "none",
    source: params.get("source") ?? "none",
    link_domain: destinationDomain(params.get("destination")),
  });
}

/**
 * Send outbound_click for every click on an /api/outbound link, including
 * ctrl-click and middle-click. Listens in the capture phase because several of
 * those links stop the click from bubbling, then waits for the click to finish
 * so a handler that cancels the navigation isn't counted.
 */
export function trackOutboundLinkClicks(): void {
  const handleClick = (e: MouseEvent) => {
    if (e.type === "auxclick" && e.button !== 1) return;
    const link = (e.target as Element | null)?.closest?.('a[href*="/api/outbound?"]');
    if (!(link instanceof HTMLAnchorElement)) return;
    const href = link.href;
    window.setTimeout(() => {
      if (!e.defaultPrevented) trackOutboundClick(href);
    }, 0);
  };

  document.addEventListener("click", handleClick, true);
  document.addEventListener("auxclick", handleClick, true);
}

const API_ERROR_THROTTLE_MS = 60_000;
const lastApiErrorReport = new Map<string, number>();

/**
 * Reduce an API URL to a stable endpoint label for reporting, e.g.
 * "/api/events?limit=50" → "/api/events" and "/api/events/series/abc/123" → "/api/events/series".
 */
export function apiErrorEndpoint(url: string): string {
  let path = url;
  try {
    path = new URL(url, "http://local").pathname;
  } catch {
    path = url.split("?")[0];
  }
  return path.split("/").filter(Boolean).slice(0, 3).join("/").replace(/^/, "/");
}

/**
 * Report a failed API response (rate limiting, server errors) to analytics.
 * Sent to Google Analytics rather than our own API, which may be the thing failing.
 * Each endpoint + status is reported at most once a minute per page.
 */
export function reportApiError(url: string, status: number): void {
  if (status === 401) return; // expected when signed out
  const endpoint = apiErrorEndpoint(url);
  const key = `${endpoint}|${status}`;
  const now = Date.now();
  const last = lastApiErrorReport.get(key);
  if (last !== undefined && now - last < API_ERROR_THROTTLE_MS) return;
  lastApiErrorReport.set(key, now);
  trackEvent("api_error", { endpoint, status });
}
